import { Host, Component, Prop, h, Element, Event, EventEmitter, State, Watch } from '@stencil/core';
import { arrow_right, open_in_new } from 'pn-design-assets/pn-assets/icons.js';
import { ripple, uuidv4 } from '@/index';

/**
 * The card component is more than just a navigational element.
 * It is a flexible component that can be used as a product item, a clickable link card, etc...
 *
 * Setting the `href` prop will transform the entire `pn-card` into a clickable element.
 * If you need content to be interactable inside the card, do not use the `href` prop.
 *
 * @slot - The default slot is the primary content of the card. This is the same area where the `text` prop is located.
 * @slot image - Set a custom image/svg/illustration. Simply use an `img` tag with the slot "image".
 * @slot tags - Add `pn-tags` that will be positioned above the `label` and `overline` if used.
 * @slot header - The header content, same area that the `label` and `overline` props will appear.
 * @slot navigation - This slot is intended for one or more `pn-button` components.
 * Only use **without** the `href` prop. Having both at the same time makes the component inaccessible.
 */
@Component({
  tag: 'pn-card',
  styleUrl: 'pn-card.scss',
})
export class PnCard {
  private readonly id: string = `pn-card-${uuidv4()}`;
  private idTitle: string = `${this.id}-title`;

  @State() visibleOutline: boolean = false;

  @Element() hostElement: HTMLElement;

  /** Headline of the card. Will act as label for the link if there is no `href-label` set. */
  @Prop() label: string;
  /** The label HTML tag. Default is `h3`. */
  @Prop() labelTag?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' = 'h3';
  /** Set a smaller text above the `label` text. */
  @Prop() overline?: string;
  /** Text content of the card. */
  @Prop() text?: string;
  /** Set a HTML id for the card. Optional and not required. */
  @Prop() cardId?: string = null;

  /**
   * Uee the horizontal card layout.
   * @category Features
   * */
  @Prop() horizontal?: boolean = false;
  /** Reverse the order of which the image will wrap. @category Features */
  @Prop() reverse?: boolean = false;
  /**
   * Select image aspect ratio. Example: '3/3', '16/9'.
   * @see {@link https://developer.mozilla.org/en-US/docs/Web/CSS/aspect-ratio CSS documentation.}
   * @category Features
   * */
  @Prop() aspectRatio?: string | '3/3' | '16/9' = '';

  /** The card link. @category Href */
  @Prop() href?: string;
  /** The link text. @category Href */
  @Prop() hrefLabel?: string;
  /** The target attribute of the link. @category Href */
  @Prop() target?: string;
  /** The rel attribute of the link. @category Href */
  @Prop() rel?: string = 'noopener noreferrer';
  /** The link icon. Defaults to `arrow_right` or `open_in_new` if `target="_blank"`. @category Href */
  @Prop() icon?: string = null;

  @Watch('cardId')
  watchCardId() {
    const id = this.cardId || this.id;
    this.idTitle = `${id}-title`;
  }

  /** Emitted when you click on the card link (if you use the `href` prop). */
  @Event() pnCard: EventEmitter<{ click: MouseEvent }>;

  componentWillLoad() {
    this.watchCardId();
  }

  private isExternal() {
    return this.target === '_blank';
  }

  private showTitle() {
    return !!this.label;
  }

  private showHref() {
    return !!this.href && (this.hrefLabel || this.showTitle());
  }

  private getTitleId() {
    return this.hrefLabel ? null : this.idTitle;
  }

  private getLabelledby() {
    return this.hrefLabel ? null : this.showTitle() ? this.idTitle : null;
  }

  private getIcon() {
    if (!!this.icon) return this.icon;
    return this.isExternal() ? open_in_new : arrow_right;
  }

  private getHeadingTag() {
    const tags = ['h2', 'h3', 'h4', 'h5', 'h6', 'p'];
    return tags.includes(this.labelTag) ? this.labelTag : 'h3';
  }

  private handleFocus({ type, target }: FocusEvent) {
    this.visibleOutline = type === 'focus' && (target as HTMLLinkElement).matches(':focus-visible');
  }

  private handleClick(event: MouseEvent) {
    ripple(event, this.hostElement, '.pn-card');
    this.pnCard.emit({ click: event });
  }

  render() {
    const TitleTag = this.getHeadingTag();

    return (
      <Host>
        <article
          id={this.cardId}
          class="pn-card"
          data-link={!!this.href}
          data-horizontal={this.horizontal}
          data-reverse={this.reverse}
          data-focus={this.visibleOutline}
        >
          <div class="pn-card-image">
            <picture class="pn-card-picture" style={{ '--pn-card-aspect-ratio': this.aspectRatio || 'unset' }}>
              <slot name="image"></slot>
            </picture>
          </div>
          <div class="pn-card-content">
            <header class="pn-card-header">
              <div class="pn-card-tags">
                <slot name="tags"></slot>
              </div>
              {!!this.overline && <p class="pn-card-label-overline">{this.overline}</p>}
              {!!this.label && (
                <TitleTag id={this.getTitleId()} class="pn-card-label">
                  {this.label}
                </TitleTag>
              )}
              <slot name="header"></slot>
            </header>
            <section class="pn-card-section">
              {!!this.text && <p class="pn-card-section-paragraph">{this.text}</p>}
              <slot></slot>
            </section>
            <nav class="pn-card-navigation">
              {this.showHref() && (
                <a
                  class="pn-card-link"
                  href={this.href}
                  target={this.target}
                  rel={this.rel}
                  aria-labelledby={this.getLabelledby()}
                  onClick={event => this.handleClick(event)}
                  onFocus={event => this.handleFocus(event)}
                  onBlur={event => this.handleFocus(event)}
                >
                  {this.hrefLabel}
                  <pn-icon color="blue700" icon={this.getIcon()} />
                </a>
              )}
              <slot name="navigation"></slot>
            </nav>
          </div>
        </article>
      </Host>
    );
  }
}
